import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import api from '@/lib/api';
import { LayoutDashboard, Target, Ticket, Package, Users, BarChart3, Settings, LogOut, Sun, Moon, ChevronLeft, ChevronRight, Bug, Wrench, Database, Store, UserCheck } from 'lucide-react';

const linkClass = (isActive) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ${isActive ? 'bg-primary/10 text-primary border-l-[3px] border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`;

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/users/pending-count').then(r => setPendingCount(r.data.count || 0)).catch(() => {});
    }
  }, [user]);

  const mainNav = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard' },
    { to: '/opportunities', icon: Target, label: 'Opportunities', testId: 'nav-opportunities' },
    { to: '/tickets', icon: Ticket, label: 'Tickets', testId: 'nav-tickets' },
    { to: '/customers', icon: Users, label: 'Customers', testId: 'nav-customers' },
    { to: '/inventory', icon: Package, label: 'Inventory', testId: 'nav-inventory' },
    { to: '/defects', icon: Bug, label: 'Defects', testId: 'nav-defects' },
    { to: '/onsite', icon: Wrench, label: 'Onsite', testId: 'nav-onsite' },
    { to: '/retailers', icon: Store, label: 'Retailers', testId: 'nav-retailers' },
    { to: '/reports', icon: BarChart3, label: 'Reports', testId: 'nav-reports' },
  ];

  return (
    <aside className={`${collapsed ? 'w-16' : 'w-56'} shrink-0 border-r border-border bg-card flex flex-col transition-all duration-200`} data-testid="sidebar">
      <div className={`p-4 border-b border-border flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && <span className="font-heading text-lg font-bold tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Fyredune</span>}
        <button onClick={() => setCollapsed(c => !c)} className="p-1 rounded hover:bg-muted transition-colors" data-testid="toggle-sidebar">
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
        {/* Main navigation */}
        {mainNav.map(item => (
          <NavLink key={item.to} to={item.to} data-testid={item.testId} className={({ isActive }) => linkClass(isActive)}>
            <item.icon size={18} strokeWidth={1.5} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {/* Admin section */}
        {user?.role === 'admin' && (
          <>
            <div className={`my-3 ${collapsed ? 'mx-1' : 'mx-1'} border-t border-border`} />
            {!collapsed && <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">Administration</p>}
            <NavLink to="/masters" data-testid="nav-masters" className={({ isActive }) => linkClass(isActive)}>
              <Database size={18} strokeWidth={1.5} />
              {!collapsed && <span>Masters</span>}
            </NavLink>
            <NavLink to="/users" data-testid="nav-user-management" className={({ isActive }) => linkClass(isActive)}>
              <div className="relative">
                <Settings size={18} strokeWidth={1.5} />
                {pendingCount > 0 && <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-destructive text-[9px] text-destructive-foreground font-bold flex items-center justify-center">{pendingCount}</span>}
              </div>
              {!collapsed && (
                <span className="flex items-center gap-2">
                  Users
                  {pendingCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-semibold">{pendingCount}</span>}
                </span>
              )}
            </NavLink>
          </>
        )}
      </div>
      <div className="p-2 border-t border-border space-y-1">
        <button onClick={toggleTheme} className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted w-full transition-colors" data-testid="toggle-theme">
          {theme === 'dark' ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
          {!collapsed && <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>}
        </button>
        <button onClick={() => { logout(); nav('/login'); }} className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted w-full transition-colors" data-testid="logout-btn">
          <LogOut size={18} strokeWidth={1.5} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
